<script setup>
    /**
     * Vue Scripts
     */
    import {computed, onMounted, ref} from "vue";
    import {globalStore} from "@pinia/global-store";
    import {searchStore} from "@pinia/search-store";
    import {dropdownFilterElms, resetSearchFilters} from "@util/util";

    /**
     * Vue Components
     */
    import InputAutocomplete from "@component/InputAutocomplete/InputAutocomplete.vue";

    const globStore = globalStore();
    const store = searchStore();

    const locationSelected = ref('')

    const {isHcps} = GLOBAL;
    const {apiKey, filterBar, resultsDefault} = TRIALS;
    const {
        content,
        dropdownItems,
        sexTooltip,
        sexTooltipIcon,
    } = filterBar;

    const {
        buttons,
        labels,
        zipCode,
    } = LABELS?.filterLabels;

    const {
        results,
        selectFilters,
        showFilters,
    } = labels;

    const {
        showResults,
        resetAll
    } = buttons
    const {perPage} = resultsDefault;

    const total = computed(() => store.resultsValues.total || 0)
    const viewport = computed(() => globStore.viewport);

    /**
     * Set up the dropdown filters to have the right
     * bindings since the plugin used breaks them
     */
    onMounted(() => dropdownFilterElms(store))

    /**
     * Takes the filters and makes the appropriate
     * API call with the new data
     */
    const setupFilters = () => store.searchApi({
        ...store.query,
        page: 1,
        isHcps,
        ppp: perPage,
        ...store.coords,
    })

    /**
     * Updates the state with the name selected and the coords for it
     * @param {String} name Name of the location searched
     * @param {Object} coords Latitude and Longitude of the location selected
     */
    const getLocation = ({name, coords}) => {
        store.$patch({
            coords,
            locationName: name || '',
            query: {
                ...store.query,
                page: 1,
                ppp: perPage,
                isHcps,
                ...coords,
            }
        });

        // Fetch the API response with the new coordinates
        store.searchApi(store.query)
    }

    /**
     * Determines showing the tooltip based on click actions
     *
     * @returns {boolean | undefined}
     */
    const toggleTooltip = () => document.getElementById('sex-tooltip')?.classList.toggle('show-tooltip');

    /**
     * Resets the search state for the component
     */
    const resetSearch = () => {
        store.$patch({
            query: {
                ...store.coords,
                page: 1,
                ppp: perPage,
                isHcps,
                sexSelected: '',
                ageSelected: '',
                statusSelected: '',
            },
        })

        resetSearchFilters()

        store.searchApi(store.query)
    }
</script>

<template>
    <div class="trials-header">
        <p
            v-if="content"
            v-html="content"
        />

        <div
            class="results-header"
            id="results-header"
        >
            <div
                class="results"
                v-html="`${total} ${results || ''}`"
            />
        </div>

        <button
            class="filters-toggle"

            aria-controls="filters-menu"
            aria-expanded="false"
            aria-haspopup="true"
        >
            <span class="icon"/>
            <span
                class="text"
                v-html="showFilters"
            />
        </button>

        <div
            class="trials-filters"
            id="filters-menu"

            aria-hidden="true"
        >
            <div class="mobile-header">
                <p v-html="selectFilters"/>
            </div>

            <div class="form-items">

                <div
                    v-if="dropdownItems"
                    class="select-items"
                >
                    <div
                        v-for="({label, values}, key) in dropdownItems"

                        :data-source="`${key}Selected`"
                        :key="key"

                        class="select-item"
                    >

                    <div class="sex-label">
                        <label
                            :for="key"
                            v-html="label"
                        />

                        <div v-if="key === 'sex' && sexTooltip"
                             class="sex-tooltip"
                             id="sex-tooltip"

                             @keydown.enter.prevent="toggleTooltip"
                             @click.prevent="toggleTooltip"
                        >
                            <div
                                class="sex-tooltip__icon"
                            >
                                <img
                                    :src="sexTooltipIcon"
                                    title="Tooltip Icon"
                                    alt="Tooltip Icon"
                                />
                            </div>
                            <div
                                id="sex-tooltip__content"
                                class="sex-tooltip__content"
                            >
                                <p v-html="sexTooltip" />
                            </div>
                        </div>
                    </div>


                        <select
                            :name="key"
                            :id="`${key}-select`"
                        >
                            <option
                                v-for="({name, slug}, index) in values"
                                v-html="name"

                                :key="index"
                                :selected="index === 0"
                                :value="slug"
                            />
                        </select>

                    </div>
                </div>

                <div class="inputs">
                    <div
                        v-if="viewport === 'desktop'"
                        class="input-item"
                    >

                        <InputAutocomplete
                            id="google-location-search"

                            :api-key="apiKey"
                            :label="zipCode?.label"
                            :placeholder="zipCode?.placeholder"
                            select-on-enter
                            :value="locationSelected"

                            @getLatLng="getLocation"
                        />

                    </div>

                    <div class="btn-submit">
                        <input
                            :value="showResults"
                            type="submit"

                            @click.prevent="setupFilters"
                        />
                    </div>
                </div>

            </div>

            <button
                class="reset"
                v-html="resetAll"

                @click.prevent="resetSearch"
            />

        </div>

    </div>
</template>

<script>
    export default {
        name: "Search Filter"
    }
</script>

<style lang="scss" scoped>
</style>
